import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Users } from "lucide-react";
import { AddDonorDialog } from "@/components/collective/AddDonorDialog";
import { RecordContributionDialog } from "@/components/collective/RecordContributionDialog";
import { toast } from "sonner";

export default function Donors() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showContributionDialog, setShowContributionDialog] = useState(false);
  const [selectedDonorId, setSelectedDonorId] = useState<string | null>(null);

  const { data: donors, isLoading } = useQuery({
    queryKey: ["collective-donors"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get user's organization
      const { data: orgMember } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", user.id)
        .single();

      if (!orgMember) throw new Error("No organization found");

      // Get donors with contribution stats
      const { data: donorsData, error } = await supabase
        .from("collective_donors")
        .select(`
          *,
          collective_contributions (
            amount,
            contribution_date
          )
        `)
        .eq("organization_id", orgMember.organization_id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Calculate stats for each donor
      return donorsData?.map(donor => {
        const contributions = donor.collective_contributions || [];
        const lastContribution = contributions.length > 0
          ? new Date(Math.max(...contributions.map(c => new Date(c.contribution_date).getTime())))
          : null;

        return {
          ...donor,
          contribution_count: contributions.length,
          last_contribution_date: lastContribution,
        };
      }) || [];
    },
  });

  const filteredDonors = donors?.filter(donor =>
    donor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    donor.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    donor.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTierColor = (tier: string) => {
    const colors = {
      platinum: "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100",
      gold: "bg-yellow-100 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-100",
      silver: "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100",
      bronze: "bg-orange-100 text-orange-900 dark:bg-orange-900 dark:text-orange-100",
      supporter: "bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100",
    };
    return colors[tier as keyof typeof colors] || "bg-secondary";
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "Never";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Donor Management</h1>
            <p className="text-muted-foreground">Track and manage your collective donors</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowContributionDialog(true)}>
              Record Contribution
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Donor
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Donors</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{donors?.length || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Lifetime Contributions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(donors?.reduce((sum, d) => sum + d.total_lifetime_contributions, 0) || 0)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Year</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(donors?.reduce((sum, d) => sum + d.total_ytd_contributions, 0) || 0)}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search donors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading donors...</div>
            ) : filteredDonors?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No donors found. Add your first donor to get started.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-right">Lifetime Total</TableHead>
                    <TableHead className="text-right">Contributions</TableHead>
                    <TableHead>Last Gift</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDonors?.map((donor) => (
                    <TableRow
                      key={donor.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/collective/donors/${donor.id}`)}
                    >
                      <TableCell className="font-medium">{donor.name}</TableCell>
                      <TableCell>{donor.email}</TableCell>
                      <TableCell>{donor.company || "—"}</TableCell>
                      <TableCell>
                        <Badge className={getTierColor(donor.tier)}>
                          {donor.tier}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(donor.total_lifetime_contributions)}
                      </TableCell>
                      <TableCell className="text-right">{donor.contribution_count}</TableCell>
                      <TableCell>{formatDate(donor.last_contribution_date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <AddDonorDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
      <RecordContributionDialog
        open={showContributionDialog}
        onOpenChange={setShowContributionDialog}
        preselectedDonorId={selectedDonorId}
      />
    </DashboardLayout>
  );
}
